import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import memberService from '../../services/memberService';
import expenseService from '../../services/expenseService';
import { useTenant } from '../../contexts/TenantContext';

// Import Material UI components
import {
  Box, Grid, Typography, Card, CardContent, Avatar,
  Paper, Divider, Button, LinearProgress, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Alert, CardActions, 
  Dialog, DialogActions, DialogContent, DialogTitle, Menu, Backdrop,
  MenuItem, IconButton, Container, FormControl, InputLabel, Select,
  TextField, InputAdornment, Autocomplete, Snackbar
} from '@mui/material';
// Import Material UI icons
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  List as ListIcon,
  ArrowForward as ArrowForwardIcon,
  CreditCard as CreditCardIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  AccountBalance as AccountBalanceIcon,
  LocalAtm as LocalAtmIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
  ReceiptLong as ReceiptLongIcon,
  Category as CategoryIcon,
  Email as EmailIcon,
  Subject as SubjectIcon,
  Info as InfoIcon,
  Send as SendIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';

// Import PDF utilities
import { generatePdfFromElement } from '../../utils/pdfUtils';
import AdminFinancialReport from '../../components/reports/AdminFinancialReport';

// Add the StatCard component from SuperAdminDashboard
const StatCard = ({ title, value, icon, color }) => (
  <Card elevation={2} sx={{ 
    height: '100%', 
    borderLeft: `4px solid ${color}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: 3
    }
  }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box display="flex" alignItems="center">
        <Avatar sx={{ 
          bgcolor: `${color}20`, 
          color: color,
          width: { xs: 40, sm: 45 },
          height: { xs: 40, sm: 45 }
        }}>
          {icon}
        </Avatar>
        <Box ml={2} sx={{ width: 'calc(100% - 60px)' }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 'medium',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  
  // Dashboard metrics state
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    pendingMembers: 0,
    expiredMembers: 0,
    totalPayments: 0,
    totalRevenue: 0,
    pendingInvoices: 0,
    totalOutstandingDebts: 0,
    recentTransactions: []
  });
  const [recentMembers, setRecentMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add new state for PDF printing and financial data
  const financialReportRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printReportType, setPrintReportType] = useState('all');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [memberTransactions, setMemberTransactions] = useState({});
  const [memberDebts, setMemberDebts] = useState({});
  const [loadingFinancialData, setLoadingFinancialData] = useState(false);
  const [financialSummary, setFinancialSummary] = useState({
    totalPaymentsReceived: 0,
    totalDebts: 0,
    totalMoneySent: 0,
  });
  
  // Add expenses state
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expenseStats, setExpenseStats] = useState({
    totalExpenses: 0,
    thisMonthExpenses: 0,
    thisYearExpenses: 0,
    categoryCounts: {}
  });

  // Add state for email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailOptions, setEmailOptions] = useState({
    reportType: 'all',
    recipientType: 'all',
    selectedMembers: [],
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Add state for snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setError(null);
        setLoading(true);
        console.log('Fetching dashboard data...');
        
        const response = await memberService.getDashboardStats();
        console.log('Dashboard data received:', response);
        
        // Extract the actual data from the response and log each step
        let statsData = {};
        if (response?.success && response?.data) {
          statsData = response.data;
          console.log('Using response.data:', statsData);
        } else if (response?.stats) {
          statsData = response.stats;
          console.log('Using response.stats:', statsData);
        } else if (response?.data?.stats) {
          statsData = response.data.stats;
          console.log('Using response.data.stats:', statsData);
        } else if (response && typeof response === 'object') {
          statsData = response;
          console.log('Using direct response object:', statsData);
        }
        
        // Log the financial values specifically to debug
        console.log('Financial values before processing:');
        console.log('- totalPayments:', statsData.totalPayments);
        console.log('- totalRevenue:', statsData.totalRevenue);
        console.log('- outstandingAmount:', statsData.outstandingAmount);
        console.log('- totalOutstandingDebts:', statsData.totalOutstandingDebts);
        
        // Try to find financial values in different possible locations
        const totalPaymentsValue = statsData.totalPayments || 
                                  statsData.totalRevenue || 
                                  statsData.payments?.total || 
                                  statsData.summary?.totalPayments ||
                                  0;
                                  
        const outstandingDebtsValue = statsData.outstandingAmount || 
                                     statsData.totalOutstandingDebts || 
                                     statsData.debts?.total ||
                                     statsData.summary?.totalOutstandingDebts ||
                                     0;
                                     
        console.log('Extracted payment value:', totalPaymentsValue);
        console.log('Extracted debts value:', outstandingDebtsValue);
        
        // Ensure all numeric values are properly converted from strings if needed
        const newStats = {
          totalMembers: Number(statsData.totalMembers || 0),
          activeMembers: Number(statsData.activeMembers || 0),
          pendingMembers: Number(statsData.pendingMembers || 0),
          expiredMembers: Number(statsData.expiredMembers || 0),
          totalPayments: parseFloat(totalPaymentsValue) || 0,
          totalRevenue: parseFloat(totalPaymentsValue) || 0,
          pendingInvoices: Number(statsData.pendingInvoices || 0),
          totalOutstandingDebts: parseFloat(outstandingDebtsValue) || 0,
          recentTransactions: Array.isArray(statsData.recentTransactions) ? statsData.recentTransactions : []
        };
        
        console.log('Final stats object:', newStats);
        setStats(newStats);
        
        // Handle different member response structures
        let membersData = [];
        if (response?.recentMembers) {
          membersData = response.recentMembers;
        } else if (response?.data?.recentMembers) {
          membersData = response.data.recentMembers;
        } else if (response?.members) {
          membersData = response.members;
        }
        
        setRecentMembers(Array.isArray(membersData) ? membersData : []);
        
        // Fetch expenses
        fetchExpenses();
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Function to fetch expenses data
  const fetchExpenses = async () => {
    try {
      setLoadingExpenses(true);
      const response = await expenseService.getExpenses();
      
      let expensesData = [];
      if (response && response.data) {
        expensesData = Array.isArray(response.data) ? response.data : response.data.expenses || [];
      } else if (Array.isArray(response)) {
        expensesData = response;
      }
      
      setExpenses(expensesData);
      
      // Calculate expense statistics
      calculateExpenseStats(expensesData);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      // Don't show error, just log it
    } finally {
      setLoadingExpenses(false);
    }
  };

  const calculateExpenseStats = (expensesData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const categoryCounts = {};
    let totalExpenses = 0;
    let thisMonthExpenses = 0;
    let thisYearExpenses = 0;
    
    expensesData.forEach(expense => {
      const amount = parseFloat(expense.amount) || 0;
      totalExpenses += amount;
      
      // Add to category count
      if (categoryCounts[expense.category]) {
        categoryCounts[expense.category].count += 1;
        categoryCounts[expense.category].amount += amount;
      } else {
        categoryCounts[expense.category] = {
          count: 1,
          amount: amount
        };
      }
      
      // Check if expense is in current month/year
      const expenseDate = new Date(expense.date);
      if (expenseDate.getFullYear() === currentYear) {
        thisYearExpenses += amount;
        
        if (expenseDate.getMonth() === currentMonth) {
          thisMonthExpenses += amount;
        }
      }
    });
    
    setExpenseStats({
      totalExpenses,
      thisMonthExpenses,
      thisYearExpenses,
      categoryCounts
    });
  };

  // New function to fetch comprehensive financial data for all members
  const fetchFinancialData = async () => {
    try {
      setLoadingFinancialData(true);
      console.log('Fetching financial data for report...');
      
      // 1. Get all members
      const membersResponse = await memberService.getAllMembers(1, 1000);
      console.log('Members data received:', membersResponse);
      
      let members = [];
      if (membersResponse && membersResponse.data) {
        if (Array.isArray(membersResponse.data)) {
          members = membersResponse.data;
        } else if (membersResponse.data.data) {
          members = membersResponse.data.data;
        }
      } else if (Array.isArray(membersResponse)) {
        members = membersResponse;
      }
      
      setAllMembers(members);
      console.log(`Loaded ${members.length} members`);
      
      // 2. Initialize data structures
      const transactions = {};
      const debts = {};
      let totalPayments = 0;
      let totalDebts = 0;
      let totalSent = 0;
      
      // 3. Fetch data for each member in batches
      const batchSize = 5;
      for (let i = 0; i < members.length; i += batchSize) {
        const memberBatch = members.slice(i, i + batchSize);
        
        // Process members in parallel
        await Promise.all(memberBatch.map(async (member) => {
          // Extract payments from member data
          const payments = member.payments || [];
          const memberPaymentsTotal = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
          totalPayments += memberPaymentsTotal;
          
          // Fetch debts
          try {
            const debtsResponse = await memberService.getMemberDebts(member._id);
            console.log(`Fetched debts for member ${member._id}:`, debtsResponse);
            
            let memberDebts = [];
            if (debtsResponse && debtsResponse.data) {
              if (Array.isArray(debtsResponse.data)) {
                memberDebts = debtsResponse.data;
              } else if (debtsResponse.data.debts) {
                memberDebts = debtsResponse.data.debts;
              }
            } else if (Array.isArray(debtsResponse)) {
              memberDebts = debtsResponse;
            }
            
            debts[member._id] = memberDebts;
            
            // Calculate active debts
            const activeDebts = memberDebts.filter(d => d.status === 'active');
            const memberDebtsTotal = activeDebts.reduce((sum, d) => sum + (d.amount || 0), 0);
            totalDebts += memberDebtsTotal;
          } catch (e) {
            console.warn(`Error fetching debts for member ${member._id}:`, e);
            debts[member._id] = [];
          }
          
          // Fetch transactions
          try {
            const transactionsResponse = await memberService.getMemberTransactions(member._id);
            console.log(`Fetched transactions for member ${member._id}:`, transactionsResponse);
            
            let memberTransactions = [];
            if (transactionsResponse && transactionsResponse.data) {
              if (Array.isArray(transactionsResponse.data)) {
                memberTransactions = transactionsResponse.data;
              } else if (transactionsResponse.data.transactions) {
                memberTransactions = transactionsResponse.data.transactions;
              }
            } else if (Array.isArray(transactionsResponse)) {
              memberTransactions = transactionsResponse;
            }
            
            transactions[member._id] = memberTransactions;
            
            // Calculate total sent to this member
            const memberSentTotal = memberTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
            totalSent += memberSentTotal;
          } catch (e) {
            console.warn(`Error fetching transactions for member ${member._id}:`, e);
            transactions[member._id] = [];
          }
        }));
      }
      
      // 4. Set all the state
      setMemberTransactions(transactions);
      setMemberDebts(debts);
      setFinancialSummary({
        totalPaymentsReceived: totalPayments,
        totalDebts: totalDebts,
        totalMoneySent: totalSent,
      });
      
      // Also fetch expenses if we haven't already
      if (expenses.length === 0) {
        await fetchExpenses();
      }
      
    } catch (error) {
      console.error('Error fetching financial data:', error);
      setError('Failed to load complete financial data. Report may be incomplete.');
    } finally {
      setLoadingFinancialData(false);
    }
  };

  const handlePrintMenuOpen = (event) => {
    setPrintMenuAnchor(event.currentTarget);
    
    // We'll only fetch the complete financial data when the user wants to print
    if (allMembers.length === 0) {
      fetchFinancialData();
    }
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSelectPrintReport = (type) => {
    setPrintReportType(type);
    setPrintDialogOpen(true);
    handlePrintMenuClose();
    
    // If we haven't fetched data yet, do it now
    if (allMembers.length === 0) {
      fetchFinancialData();
    }
  };

  const handleEmailReportOption = () => {
    console.log('Email report option selected');
    
    // If we haven't fetched data yet, do it now
    if (allMembers.length === 0) {
      fetchFinancialData().then(() => {
        setEmailDialogOpen(true);
      });
    } else {
      setEmailDialogOpen(true);
    }
  };

  const handlePrintReport = async () => {
    try {
      setIsPrinting(true);
      
      if (!financialReportRef.current) {
        throw new Error('Report component not found');
      }

      const fileName = `${tenant?.name?.replace(/\s+/g, '_') || 'organization'}_financial_report_${new Date().toISOString().split('T')[0]}`;
      
      await generatePdfFromElement(
        financialReportRef.current,
        fileName,
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setPrintDialogOpen(false);
            setSnackbar({
              open: true,
              message: 'Financial report generated successfully!',
              severity: 'success'
            });
          },
          onError: (error) => {
            console.error('PDF generation error:', error);
            setIsPrinting(false);
            setSnackbar({
              open: true,
              message: 'Failed to generate financial report: ' + error.message,
              severity: 'error'
            });
          }
        }
      );
    } catch (error) {
      console.error('Error printing report:', error);
      setIsPrinting(false);
      setSnackbar({
        open: true,
        message: 'Failed to generate financial report: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleSendReportEmail = async () => {
    try {
      setSendingEmail(true);
      console.log('Sending email report...', emailOptions);
      
      // Import the email service
      const emailService = await import('../../services/emailService').then(module => module.default);
      
      // Prepare data for API call
      const reportData = {
        reportType: emailOptions.reportType,
        recipientType: emailOptions.recipientType,
        selectedMembers: emailOptions.recipientType === 'selected' 
          ? emailOptions.selectedMembers.map(member => member._id) 
          : [],
        subject: emailOptions.subject || `Financial Report for ${tenant?.name || 'Your Organization'}`,
        message: emailOptions.message
      };
      
      // Send the financial report emails
      const response = await emailService.sendFinancialReportEmail(reportData);
      
      console.log('Email sent response:', response);
      
      if (response.success) {
        setEmailDialogOpen(false);
        setSnackbar({
          open: true,
          message: `Financial report emails sent successfully to ${response.results?.success?.length || 0} recipients${
            response.results?.failed?.length ? ` (${response.results.failed.length} failed)` : ''
          }`,
          severity: 'success'
        });
      } else {
        throw new Error(response.message || 'Failed to send emails');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send email: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !recentMembers.length && !stats.totalMembers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">Error</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button 
            onClick={() => window.location.reload()} 
            color="error"
            sx={{ mt: 1 }}
            size="small"
          >
            Refresh page
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4 }}> {/* Changed margin top to be responsive */}
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2, 
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <DashboardIcon sx={{ mr: 1 }} /> Dashboard
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              Overview of your {tenant?.name || 'organization'}'s membership data
            </Typography>
          </Box>
          
          {/* Add Print Button */}
          <Button
            onClick={handlePrintMenuOpen}
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{ 
              color: 'white', 
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Financial Report
          </Button>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} /> Membership Overview
          </Typography>
          <Button
            component={Link}
            to="/admin/members"
            variant="contained"
            color="primary"
            startIcon={<ListIcon />}
          >
            Manage Members
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Members"
              value={stats.totalMembers}
              icon={<PeopleIcon />}
              color="#3f51b5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Active Members"
              value={stats.activeMembers}
              icon={<CheckCircleIcon />}
              color="#4CAF50"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Pending Members"
              value={stats.pendingMembers}
              icon={<PendingIcon />}
              color="#FF9800"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Expired Members"
              value={stats.expiredMembers}
              icon={<WarningIcon />}
              color="#F44336"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Payments"
              value={`$${Number(stats.totalPayments || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              icon={<CreditCardIcon />}
              color="#009688"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Outstanding Debts"
              value={`$${Number(stats.totalOutstandingDebts || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              icon={<AttachMoneyIcon />}
              color="#F44336"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Add Expenses Overview */}
      <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <LocalAtmIcon sx={{ mr: 1, color: 'error.main' }} /> Expenses Overview
          </Typography>
          <Button
            component={Link}
            to="/admin/expenses"
            variant="contained"
            color="error"
            startIcon={<AddIcon />}
          >
            Manage Expenses
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Expenses"
              value={`$${expenseStats.totalExpenses.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              icon={<ReceiptLongIcon />}
              color="#ff9800"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={`This Month (${new Date().toLocaleString('default', { month: 'long' })})`}
              value={`$${expenseStats.thisMonthExpenses.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              icon={<CategoryIcon />}
              color="#9c27b0"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Net Revenue"
              value={`$${(Number(stats.totalPayments) - Number(expenseStats.totalExpenses)).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              icon={<ReceiptIcon />}
              color="#e53935"
            />
          </Grid>
        </Grid>
        
        {expenses.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Recent Expenses
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableRow>
                    <TableCell width="15%">Date</TableCell>
                    <TableCell width="20%">Category</TableCell>
                    <TableCell width="15%">Amount</TableCell>
                    <TableCell width="50%">Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.slice(0, 5).map((expense, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{new Date(expense.date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip 
                          label={expense.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          size="small"
                          sx={{ 
                            bgcolor: (() => {
                              switch(expense.category) {
                                case 'utilities': return 'rgba(33, 150, 243, 0.1)';
                                case 'rent': return 'rgba(156, 39, 176, 0.1)';
                                case 'salaries': return 'rgba(76, 175, 80, 0.1)';
                                case 'supplies': return 'rgba(255, 152, 0, 0.1)';
                                case 'marketing': return 'rgba(233, 30, 99, 0.1)';
                                case 'events': return 'rgba(0, 188, 212, 0.1)';
                                default: return 'rgba(158, 158, 158, 0.1)';
                              }
                            })(),
                            color: (() => {
                              switch(expense.category) {
                                case 'utilities': return 'rgb(25, 118, 210)';
                                case 'rent': return 'rgb(123, 31, 162)';
                                case 'salaries': return 'rgb(56, 142, 60)';
                                case 'supplies': return 'rgb(245, 124, 0)';
                                case 'marketing': return 'rgb(173, 20, 87)';
                                case 'events': return 'rgb(0, 151, 167)';
                                default: return 'rgb(97, 97, 97)';
                              }
                            })()
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'error.main', fontWeight: 'medium' }}>
                        {`$${expense.amount.toFixed(2)}`}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          sx={{ 
                            maxWidth: '100%', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}
                          title={expense.description}
                        >
                          {expense.description || 'No description'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                component={Link} 
                to="/admin/expenses"
                color="error"
                endIcon={<ArrowForwardIcon />}
              >
                View All Expenses
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Recent Members Section */}
      <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} /> Recent Members
        </Typography>
        
        {recentMembers && recentMembers.length > 0 ? (
          <TableContainer>
            <Table size="medium">
              <TableHead sx={{ bgcolor: 'rgba(63, 81, 181, 0.08)' }}>
                <TableRow>
                  <TableCell>Member Name</TableCell>
                  <TableCell>Membership #</TableCell>
                  <TableCell>Join Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentMembers.slice(0, 5).map((member, index) => (
                  <TableRow key={member._id || index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: 'primary.light',
                            width: 36,
                            height: 36
                          }}
                        >
                          {member.userId?.firstName?.[0]}{member.userId?.lastName?.[0]}
                        </Avatar>
                        <Typography>
                          {member.userId?.firstName} {member.userId?.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{member.membershipNumber || 'N/A'}</TableCell>
                    <TableCell>{new Date(member.joinDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={member.status?.toUpperCase() || 'UNKNOWN'} 
                        size="small"
                        color={
                          member.status === 'active' ? 'success' : 
                          member.status === 'pending' ? 'warning' : 
                          'error'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        component={Link}
                        to={`/admin/members/${member._id}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowForwardIcon />}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No recent members found</Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            component={Link} 
            to="/admin/members"
            color="primary"
            endIcon={<ArrowForwardIcon />}
          >
            View All Members
          </Button>
        </Box>
      </Paper>

      {/* Recent Transactions Section */}
      {stats.recentTransactions && stats.recentTransactions.length > 0 && (
        <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} /> Recent Transactions
          </Typography>
          
          <TableContainer>
            <Table size="medium">
              <TableHead sx={{ bgcolor: 'rgba(0, 150, 136, 0.08)' }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.recentTransactions.slice(0, 5).map((transaction, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {new Date(transaction.date || transaction.transactionDate || transaction.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {transaction.memberName || 
                       (transaction.member && `${transaction.member.firstName} ${transaction.member.lastName}`) || 
                       'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#009688', fontWeight: 'medium' }}>
                      {`$${(transaction.amount || 0).toFixed(2)}`}
                    </TableCell>
                    <TableCell>
                      {(transaction.method || transaction.paymentMethod || 'N/A').replace('_', ' ')}
                    </TableCell>
                    <TableCell>{transaction.description || transaction.notes || 'No description'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              component={Link} 
              to="/admin/transactions"
              color="primary"
              endIcon={<ArrowForwardIcon />}
            >
              View All Transactions
            </Button>
          </Box>
        </Paper>
      )}

      {/* Quick Access Cards Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Workflow Dashboard Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.15)',
              },
              position: 'relative'
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              top: 12, 
              right: 12, 
              backgroundColor: 'rgba(63, 81, 181, 0.1)',
              borderRadius: '50%',
              p: 1
            }}>
              <BarChartIcon fontSize="large" color="primary" />
            </Box>
            <CardContent sx={{ flexGrow: 1, pt: 4 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Workflow Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Access comprehensive analytics and insights for all workflow processes.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, pt: 0 }}>
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => navigate('/admin/workflow-dashboard')}
                startIcon={<DashboardIcon />}
                sx={{ borderRadius: 1 }}
              >
                View Dashboard
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Print Menu */}
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Select Report Type
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleSelectPrintReport('all')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#3f51b5' }} />
          Complete Financial Report
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('payments')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#009688' }} />
          Payment Summary
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('expenses')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#f44336' }} />
          Expenses Summary
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('debts')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#ff9800' }} />
          Outstanding Debts
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('members')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#4caf50' }} />
          Member Breakdown
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={() => handleEmailReportOption()}>
          <EmailIcon fontSize="small" sx={{ mr: 2, color: '#1976d2' }} />
          Email Financial Report
        </MenuItem>
      </Menu>
      
      {/* Print Preview Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => !isPrinting && setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrintIcon sx={{ mr: 1 }} />
            Organization Financial Report
          </Box>
          {!isPrinting && (
            <IconButton
              onClick={() => setPrintDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '70vh'
        }}>
          {loadingFinancialData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '70vh' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6">Loading financial data...</Typography>
              <Typography variant="body2" color="text.secondary">
                This might take a moment as we gather data for all members
              </Typography>
            </Box>
          ) : (
            <Box sx={{ my: 2, width: '100%', overflow: 'auto' }}>
              <AdminFinancialReport
                ref={financialReportRef}
                tenant={{
                  name: tenant?.name,  // Explicitly pass name as a direct property
                  ...tenant            // Also pass the full tenant object
                }}
                members={allMembers}
                memberDebts={memberDebts}
                memberTransactions={memberTransactions}
                financialSummary={financialSummary}
                expenses={expenses}
                stats={stats}
                reportType={printReportType}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting || loadingFinancialData}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintReport}
            disabled={isPrinting || loadingFinancialData}
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            color="primary"
          >
            {isPrinting ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Email Report Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => !sendingEmail && setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} />
            Email Financial Report
          </Box>
          {!sendingEmail && (
            <IconButton
              onClick={() => setEmailDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loadingFinancialData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6">Loading financial data...</Typography>
              <Typography variant="body2" color="text.secondary">
                This might take a moment as we gather data for all members
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                Configure Email Settings
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      value={emailOptions.reportType}
                      label="Report Type"
                      onChange={(e) => setEmailOptions({...emailOptions, reportType: e.target.value})}
                    >
                      <MenuItem value="all">Complete Financial Report</MenuItem>
                      <MenuItem value="payments">Payment Summary</MenuItem>
                      <MenuItem value="expenses">Expenses Summary</MenuItem>
                      <MenuItem value="debts">Outstanding Debts</MenuItem>
                      <MenuItem value="members">Member Breakdown</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Recipients</InputLabel>
                    <Select
                      value={emailOptions.recipientType}
                      label="Recipients"
                      onChange={(e) => {
                        const newType = e.target.value;
                        setEmailOptions({
                          ...emailOptions, 
                          recipientType: newType,
                          // Reset selected members if switching to 'all'
                          selectedMembers: newType === 'all' ? [] : emailOptions.selectedMembers
                        });
                      }}
                    >
                      <MenuItem value="all">All Active Members</MenuItem>
                      <MenuItem value="selected">Selected Members</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {emailOptions.recipientType === 'selected' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={allMembers.filter(m => m.userId?.email)}
                      getOptionLabel={(option) => 
                        `${option.userId?.firstName || ''} ${option.userId?.lastName || ''} (${option.userId?.email || 'No email'})`
                      }
                      value={emailOptions.selectedMembers}
                      onChange={(e, newValue) => 
                        setEmailOptions({...emailOptions, selectedMembers: newValue})
                      }
                      ListboxProps={{
                        style: { maxHeight: '250px' }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Recipients"
                          placeholder="Search members..."
                          variant="outlined"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} style={{ padding: '8px 16px' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            width: '100%' 
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {option.userId?.firstName} {option.userId?.lastName}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center', 
                              width: '100%' 
                            }}>
                              <Typography variant="caption" color="text.secondary">
                                {option.userId?.email || 'No email'}
                              </Typography>
                              <Chip 
                                label={option.status?.toUpperCase() || 'UNKNOWN'} 
                                size="small"
                                color={
                                  option.status === 'active' ? 'success' : 
                                  option.status === 'pending' ? 'warning' : 
                                  'error'
                                }
                                variant="outlined"
                                sx={{ ml: 1, height: '20px', '& .MuiChip-label': { px: 1, py: 0 } }}
                              />
                            </Box>
                          </Box>
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'primary.light', width: 24, height: 24, fontSize: '0.75rem' }}>
                                {option.userId?.firstName?.[0]}{option.userId?.lastName?.[0]}
                              </Avatar>
                            }
                            label={`${option.userId?.firstName} ${option.userId?.lastName}`}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Subject"
                    variant="outlined"
                    value={emailOptions.subject || `Financial Report for ${tenant?.name || 'Your Organization'}`}
                    onChange={(e) => setEmailOptions({...emailOptions, subject: e.target.value})}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SubjectIcon color="primary" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message (Optional)"
                    variant="outlined"
                    value={emailOptions.message}
                    onChange={(e) => setEmailOptions({...emailOptions, message: e.target.value})}
                    multiline
                    rows={4}
                    placeholder="Add a personal message to include with the financial report..."
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ bgcolor: 'info.light', color: 'info.contrastText', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1 }} /> 
                  Email Information
                </Typography>
                <Typography variant="body2">
                  The email will be sent from {tenant?.adminEmail || 'the administrator email'} and will include the selected financial report as the body of the email.
                </Typography>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {emailOptions.recipientType === 'all' ? (
                  <>Will send to {allMembers.filter(m => m.status === 'active' && m.userId?.email).length} active members</>
                ) : (
                  <>Selected {emailOptions.selectedMembers.length} member{emailOptions.selectedMembers.length !== 1 ? 's' : ''}</>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setEmailDialogOpen(false)}
            disabled={sendingEmail || loadingFinancialData}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendReportEmail}
            disabled={
              sendingEmail || 
              loadingFinancialData || 
              (emailOptions.recipientType === 'selected' && emailOptions.selectedMembers.length === 0)
            }
            variant="contained"
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
            color="primary"
          >
            {sendingEmail ? 'Sending...' : 'Send Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Printing Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isPrinting || loadingFinancialData}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 4,
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">
            {loadingFinancialData ? 'Loading financial data...' : 'Generating PDF...'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please wait, this may take a moment
          </Typography>
        </Box>
      </Backdrop>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DashboardPage;